import { onBeforeUnmount, onMounted, ref } from 'vue'
import { usePrefersReducedMotion } from './usePrefersReducedMotion'

/**
 * Reveal-on-scroll backed by ONE IntersectionObserver for the whole page.
 *
 * The previous version created an observer per element — roughly eighty of
 * them on this page. Each observer is its own bookkeeping entry the browser
 * has to service, and they all fire in the same scroll frame. A single shared
 * observer with a WeakMap of callbacks does the same job for one entry.
 *
 * Elements are unobserved the moment they reveal, so the observed set shrinks
 * to nothing as the reader scrolls.
 */
const callbacks = new WeakMap()
let observer = null
let observed = 0

function sharedObserver() {
  if (observer) return observer

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        callbacks.get(entry.target)?.()
        release(entry.target)
      }
    },
    { threshold: 0.08, rootMargin: '0px 0px -8% 0px' }
  )

  return observer
}

function release(el) {
  if (!callbacks.has(el)) return
  callbacks.delete(el)
  observer?.unobserve(el)
  observed -= 1
  // Nothing left to watch: drop the observer so it stops being serviced.
  if (observed <= 0) {
    observer?.disconnect()
    observer = null
    observed = 0
  }
}

/**
 * @returns {{ el: import('vue').Ref<HTMLElement|null>, revealed: import('vue').Ref<boolean> }}
 */
export function useScrollReveal() {
  const el = ref(null)
  const revealed = ref(false)
  const reduced = usePrefersReducedMotion()

  onMounted(() => {
    if (reduced.value || typeof IntersectionObserver === 'undefined') {
      revealed.value = true
      return
    }
    if (!el.value) return

    callbacks.set(el.value, () => {
      revealed.value = true
    })
    observed += 1
    sharedObserver().observe(el.value)
  })

  onBeforeUnmount(() => {
    if (el.value) release(el.value)
  })

  return { el, revealed }
}
